// @core/stores/fiscalization/FiscalizationFiscalPointStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { fiscalizationApi } from '@core/api';
import type {
  DeregisterFiscalPointRequest,
  FiscalPoint,
  RegisterFiscalPointRequest,
  RegisterFiscalPointResponse,
  UpdateFiscalPointRequest,
} from '@core/types/fiscalization';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class FiscalizationFiscalPointStore {
  // List state
  items: FiscalPoint[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: FiscalPoint | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, FiscalPoint>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): FiscalPoint | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: FiscalPoint | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Deregister fiscal point
   */
  async DeregisterFiscalPoint(id: string, data: DeregisterFiscalPointRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await fiscalizationApi.DeregisterFiscalPoint(id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get fiscal point by ID
   */
  async GetFiscalPoint(id: string): Promise<FiscalPoint> {
    // Check cache first
    const cached = this.cache.get(id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.GetFiscalPoint(id);
      const item = response.fiscal_point;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List fiscal points
   */
  async ListFiscalPoints(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.ListFiscalPoints(params);

      runInAction(() => {
        this.items = response.fiscal_points?.points ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Register fiscal point
   */
  async RegisterFiscalPoint(data: RegisterFiscalPointRequest): Promise<RegisterFiscalPointResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await fiscalizationApi.RegisterFiscalPoint(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update fiscal point
   */
  async UpdateFiscalPoint(id: string, data: UpdateFiscalPointRequest): Promise<FiscalPoint> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.UpdateFiscalPoint(id, data);
      const item = response.fiscal_point;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const fiscalizationFiscalPointStore = new FiscalizationFiscalPointStore();
